import { Box, Tab } from '@mui/material';
import { MprcContainer, MprcRequestErrorReport } from 'components';
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterLine } from 'components/filter';
import { MprcChipTabs } from 'components/MprcChipTabs';
import { MprcTextFieldColor } from 'components/MprcTextFieldColor';
import { FeedHistoryType, FeedType } from 'generated/CoreApi_v1';
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import LibUtils from 'lib/LibUtils';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FeedContainer, FeedManager } from '../components';
import { FeedManagerHandle } from '../components/FeedManager';

////////////////////////////////////////////////////////////////////////////////

interface IFilter {
    searchText: string;
    type: FeedHistoryType;
}

////////////////////////////////////////////////////////////////////////////////

const defaultFilterOptions = {
    type: FeedHistoryType.Viewed,
    searchText: "",
};

////////////////////////////////////////////////////////////////////////////////

export const FeedHistory: React.FC = () => {

    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();

    const { t } = useTranslation("knowledge");

    const [searchText, setSearchText] = useState<string>(location.state?.filter?.searchText ?? "");
    const [totalItems, setTotalItems] = useState<number | null | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<IFilter>(location.state?.filter ?? defaultFilterOptions);
    const [prevPub, setPrevPub] = useState<string | undefined>(location.state?.publicationId);
    const [viewError, setViewError] = useState<any>();

    const searchRef = useRef<HTMLInputElement>(undefined);
    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const filtered = filterOptions.searchText.length > 0;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        setLoading(true);
        try {
            var result = await api.v1.knowledgeFeed.getFeedHistory({
                startIndex,
                endIndex,
                startPublicationId: location.state?.publicationId ?? null,
                ...filterOptions
            });
            setTotalItems(result.data.totalRows);
            return result.data.items;
        }
        finally {
            setLoading(false);
        }
    };

    ///
    const handleFilterChange = (filter: IFilter) => {
        if (!filter.searchText) setSearchText("");
        setFilterOptions(filter);
        setTotalItems(null);
    };

    ///
    const handleChoise = (option: FeedHistoryType) => {
        handleFilterChange({ ...filterOptions, type: option });
    };

    ///
    const resetFilters = () => {
        handleFilterChange({ ...filterOptions, searchText: "" });
    };

    ///
    useEffect(() => {
        if (totalItems === null) {
            navigate(document.location.search, { replace: true, state: { filter: filterOptions } });
        }
    }, [totalItems]);

    /// Retry
    useDidUpdateEffect(() => {
        if (viewError == null) feedRef.current.reset();
    }, [viewError]);

    ///
    useEffect(() => {
        if (prevPub != location.state?.publicationId) {
            setPrevPub(undefined);
        }

        feedRef.current?.reset();

    }, [location.state]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    const Header = (
        <Box>
            <MprcContainer>
                <MprcFilterBox>
                    <MprcFilterLine>
                        <MprcChipTabs
                            value={filterOptions.type}
                            onChange={(e, value) => handleChoise(value)}
                        >
                            {
                                LibUtils.convertEnumToArray(FeedHistoryType).map(item =>
                                    <Tab
                                        key={item.key}
                                        label={t(`Feed.History.${item.value}FilterText`)}
                                        value={FeedHistoryType[item.value as keyof typeof FeedHistoryType]}
                                    />
                                )}
                        </MprcChipTabs>
                    </MprcFilterLine>
                    <MprcFilterLine>

                        <MprcTextFieldColor
                            fullWidth
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            inputRef={searchRef}
                            autoFocus
                            autoComplete="off"
                            onKeyDown={(e) => {
                                if (e.keyCode == 13) handleFilterChange({ ...filterOptions, searchText: searchRef.current?.value ?? "" });
                            }}
                            placeholder={t("Feed.History.SearchTextPlaceholder")}
                        />
                    </MprcFilterLine>
                </MprcFilterBox>
            </MprcContainer>
            {
                !loading &&
                <MprcFilterCountInfo
                    text={t(filtered ? "Feed.History.RecordsFoundText" : "Feed.History.RecordsText", { count: totalItems ?? 0 })}
                    variant={filtered ? "bodyNotice" : (totalItems ?? 0) == 0 ? "captionHeader" : "caption"}
                    onResetFilter={filtered ? resetFilters : undefined}
                />
            }
        </Box >
    );

    return viewError
        ? <MprcRequestErrorReport
            error={viewError}
            onRetry={() => setViewError(null)}
        />
        : <FeedContainer
            header={Header}
            feed={
                <FeedManager
                    onGobackTop={() => setTotalItems(null)}
                    previousPublicationId={prevPub}
                    feedType={FeedType.History}
                    onGetData={onGetData}
                    onError={(error) => setViewError(error)}
                    ref={feedRef}
                    useInfiniteLoading
                />
            }
        />;
};
